import { useTranslation } from 'react-i18next';
import { ExternalLink, Info } from 'lucide-react';
import type { RiskAssessment, RiskFactorDto } from '@foco/contracts';

/**
 * The risk assessment.
 *
 * Section 6.3 is unambiguous: the verdict must be **explainable**. A bare "high
 * risk" is worth nothing to a user who has to justify a decision, so this panel
 * always renders the factors behind the score — each with its title, the
 * evidence that triggered it, and a link to the source of the fact.
 *
 * Two details that matter:
 *
 *   * The colour is never the only signal. Every level is paired with a word and
 *     a numeric score, because a red dot alone is invisible to a colour-blind
 *     user and useless in a printed report.
 *   * The disclaimer is not optional and not dismissible. It is rendered
 *     whenever a level is shown, because the moment a user acts on this number
 *     is the moment the disclaimer has to be on screen.
 */

const LEVEL_TONE: Record<string, string> = {
  low: 'border-risk-low/40 bg-risk-low/5 text-risk-low',
  medium: 'border-risk-medium/40 bg-risk-medium/5 text-risk-medium',
  high: 'border-risk-high/40 bg-risk-high/5 text-risk-high',
  unknown: 'border-[var(--color-border)] bg-[var(--color-surface-2)] text-[var(--color-muted-fg)]',
};

const SEVERITY_TONE: Record<RiskFactorDto['severity'], string> = {
  critical: 'bg-risk-high/15 text-risk-high',
  high: 'bg-risk-high/10 text-risk-high',
  medium: 'bg-risk-medium/10 text-risk-medium',
  low: 'bg-[var(--color-surface-2)] text-[var(--color-muted-fg)]',
};

export function RiskPanel({ risk }: { risk: RiskAssessment }): React.ReactNode {
  const { t } = useTranslation();
  const level = risk.level;

  return (
    <section
      aria-labelledby="risk-heading"
      className="mb-6 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-card)]"
    >
      <div className={`rounded-t-[var(--radius-card)] border-b p-5 ${LEVEL_TONE[level] ?? LEVEL_TONE.unknown}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 id="risk-heading" className="text-sm font-medium uppercase tracking-wide opacity-80">
              {t('lookup.risk.title')}
            </h2>
            <p className="mt-1 text-2xl font-bold">
              {t(`riskLevels.${level}`, level)}
            </p>
          </div>
          {/* The score is shown so the level is auditable against the weights,
              not a black box. */}
          <p className="text-sm opacity-80">
            {t('lookup.riskScore')}: {risk.score}
          </p>
        </div>
      </div>

      <div className="p-5">
        {risk.factors.length === 0 ? (
          <p className="text-sm text-[var(--color-muted-fg)]">
            {t('blocks.risk_assessment.noFactors')}
          </p>
        ) : (
          <>
            <h3 className="text-sm font-semibold">
              {t('blocks.risk_assessment.factors')} ({risk.factors.length})
            </h3>

            <ul className="mt-3 space-y-3">
              {risk.factors.map((factor) => (
                <li
                  key={factor.code}
                  className="rounded-[var(--radius-control)] border border-[var(--color-border)] p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${SEVERITY_TONE[factor.severity]}`}
                    >
                      {t(`riskSeverity.${factor.severity}`, factor.severity)}
                    </span>
                    <span className="font-medium">{factor.title}</span>
                  </div>

                  {factor.description && (
                    <p className="mt-1.5 text-sm text-[var(--color-muted-fg)]">
                      {factor.description}
                    </p>
                  )}

                  {/* Evidence: the concrete values behind the verdict, so a user
                      can verify the claim instead of trusting it. */}
                  {Object.keys(factor.evidence ?? {}).length > 0 && (
                    <dl className="mt-2 space-y-0.5 text-xs">
                      <dt className="text-[var(--color-muted-fg)]">
                        {t('blocks.risk_assessment.evidence')}
                      </dt>
                      <dd className="font-mono text-[11px] text-[var(--color-muted-fg)]">
                        {Object.entries(factor.evidence)
                          .filter(([key]) => key !== 'sourceCode' && key !== 'sourceUrl')
                          .map(([key, value]) => `${key}: ${formatEvidence(value)}`)
                          .join(' · ')}
                      </dd>
                    </dl>
                  )}

                  {factor.sourceUrl && (
                    <a
                      href={factor.sourceUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="mt-2 inline-flex items-center gap-1 text-xs text-brand-600 hover:underline"
                    >
                      {t('blocks.risk_assessment.source')}
                      <ExternalLink aria-hidden className="h-3 w-3" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Mandatory and always present when a verdict is on screen. */}
        <p className="mt-4 flex items-start gap-2 border-t border-[var(--color-border)] pt-3 text-xs text-[var(--color-muted-fg)]">
          <Info aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {risk.disclaimer}
        </p>
      </div>
    </section>
  );
}

function formatEvidence(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') {
    // A nested object (for example the first matched entry) is summarised
    // rather than dumped: the card is a document, not a debug view.
    const entries = Object.entries(value as Record<string, unknown>).slice(0, 3);
    return entries.map(([key, inner]) => `${key}=${String(inner)}`).join(', ');
  }
  return String(value);
}
